import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, Wallet, Percent, ReceiptIndianRupee, Scroll,
  Building2, ListPlus, AlertTriangle, Trash2, Save, Plus, Landmark, Download,
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { API, useAuth } from '../App';
import { istTodayStr } from '../lib/ist';

const HEAD = { fontFamily: "'Outfit', 'Manrope', sans-serif" };
const inr = (n) => `${n < 0 ? '−' : ''}₹${Math.abs(Math.round(n || 0)).toLocaleString('en-IN')}`;
const CATEGORIES = ['maintenance', 'transport', 'marketing', 'supplies', 'misc'];

export default function ProfitabilityTab({ token, range, selectedKiosks, kiosks }) {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!range) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start: new Date(range.start).toISOString(),
        end: new Date(range.end).toISOString(),
      });
      if (range.prevStart != null) {
        params.set('prev_start', new Date(range.prevStart).toISOString());
        params.set('prev_end', new Date(range.prevEnd).toISOString());
      }
      if (selectedKiosks.length) params.set('kiosk_ids', selectedKiosks.join(','));
      const trendParams = new URLSearchParams({
        start: new Date(range.start).toISOString(),
        end: new Date(range.end).toISOString(),
      });
      if (selectedKiosks.length) trendParams.set('kiosk_ids', selectedKiosks.join(','));
      const [r, tr] = await Promise.all([
        axios.get(`${API}/profit/overview?${params}`, { headers }),
        axios.get(`${API}/profit/trend?${trendParams}`, { headers }),
      ]);
      setData(r.data);
      setTrend(tr.data);
    } catch (e) { /* silent */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, selectedKiosks, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">{[...Array(6)].map((_, i) => <div key={i} className="bg-white border border-slate-200 rounded-xl h-28 animate-pulse" />)}</div>;
  }
  if (!data) return null;
  const t = data.totals;
  const p = data.previous;

  const kpis = [
    { key: 'net_profit', label: 'Net Profit', value: inr(t.net_profit), accent: t.net_profit >= 0 ? 'from-emerald-500 to-teal-500' : 'from-rose-500 to-red-500', icon: Wallet, delta: p ? [t.net_profit, p.net_profit] : null },
    { key: 'margin', label: 'Profit Margin', value: t.margin_pct == null ? '—' : `${t.margin_pct}%`, accent: 'from-violet-500 to-purple-500', icon: Percent },
    { key: 'gst', label: 'GST Payable', value: inr(t.gst), accent: 'from-indigo-500 to-blue-500', icon: Landmark },
    { key: 'fees', label: 'Razorpay Fees', value: inr(t.fees), accent: 'from-sky-500 to-cyan-500', icon: ReceiptIndianRupee },
    { key: 'media', label: 'Roll Cost', value: inr(t.media_cost), accent: 'from-amber-500 to-orange-500', icon: Scroll },
    { key: 'rent', label: 'Rent', value: inr(t.rent), accent: 'from-pink-500 to-rose-500', icon: Building2 },
    { key: 'expenses', label: 'Other Expenses', value: inr(t.total_expenses), accent: 'from-fuchsia-500 to-pink-500', icon: ListPlus },
  ];

  return (
    <div className="space-y-6" data-testid="profitability-tab">
      {data.warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 space-y-0.5" data-testid="profit-warnings">
          {data.warnings.map((w, i) => <div key={i} className="flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{w}</div>)}
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="profit-kpi-row">
        {kpis.map((k) => {
          const Icon = k.icon;
          let chip = null;
          if (k.delta) {
            const [cur, prev] = k.delta;
            if (!(cur === 0 && prev === 0)) {
              const pct = prev === 0 ? 100 : ((cur - prev) / Math.abs(prev)) * 100;
              const good = cur >= prev;
              chip = (
                <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${good ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {good ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {Math.abs(pct) >= 999 ? '999+' : Math.abs(pct).toFixed(0)}%
                </span>
              );
            }
          }
          return (
            <div key={k.key} className="relative overflow-hidden bg-white border border-slate-200 rounded-xl shadow-sm p-4" data-testid={`profit-kpi-${k.key}`}>
              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${k.accent}`} />
              <div className="flex items-center justify-between mb-2">
                <span className="p-1.5 rounded-lg bg-slate-50"><Icon className="w-4 h-4 text-slate-500" /></span>
                {chip}
              </div>
              <div className={`text-2xl font-bold tracking-tight ${k.key === 'net_profit' ? (t.net_profit >= 0 ? 'text-emerald-600' : 'text-rose-600') : 'text-slate-900'}`} style={HEAD}>{k.value}</div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mt-0.5">{k.label}</div>
            </div>
          );
        })}
      </div>

      <ProfitTrendChart trend={trend} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* P&L breakdown */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="pl-breakdown">
          <h3 className="text-sm font-bold text-slate-900 mb-4" style={HEAD}>P&L breakdown</h3>
          {[
            ['Gross revenue', t.revenue, false],
            ['Refunds', -t.refunds, true],
            ['GST (incl. in sales)', -t.gst, true],
            ['Razorpay fees', -t.fees, true],
            ['Roll cost', -t.media_cost, true],
            ['Rent', -t.rent, true],
            ['Booth expenses', -(t.total_expenses - t.general_expenses), true],
            ['General expenses', -t.general_expenses, true],
          ].map(([label, v, isCost]) => (
            <div
              key={label}
              title={label === 'Gross revenue'
                ? 'All successful payments (incl. late) in this window from booths currently in your booth list. The Analytics Revenue card counts ALL booths ever (incl. deleted) and uses rolling presets — so the two can differ.'
                : undefined}
              className="flex items-center justify-between py-1.5 border-b border-slate-50 text-sm"
            >
              <span className="text-slate-600">{label}</span>
              <span className={`font-semibold ${isCost && v !== 0 ? 'text-rose-600' : 'text-slate-900'}`}>{inr(v)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-3 mt-1">
            <span className="font-bold text-slate-900" style={HEAD}>Net profit</span>
            <span className={`text-xl font-bold ${t.net_profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} style={HEAD} data-testid="pl-net-profit">{inr(t.net_profit)}</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            GST {data.settings_used.gst_pct}% included in sales ({(data.settings_used.gst_pct / (100 + data.settings_used.gst_pct) * 100).toFixed(1)}% of gross, ITC not netted in P&L — see GST report) ·
            fees = {data.settings_used.commission_pct}% + 18% GST ({(data.settings_used.commission_pct * 1.18).toFixed(2)}% effective) ·
            roll cost ₹{data.settings_used.cost_per_print}/print · rent accrued daily
          </p>
        </div>

        {/* Per-booth P&L */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="booth-pl-table">
          <h3 className="text-sm font-bold text-slate-900 px-6 pt-6 pb-3" style={HEAD}>Per-booth P&L</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-slate-100 bg-slate-50/60 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 text-left">Booth</th>
                  <th className="px-4 py-2.5 text-right">Net Rev</th>
                  <th className="px-4 py-2.5 text-right">GST</th>
                  <th className="px-4 py-2.5 text-right">Fees</th>
                  <th className="px-4 py-2.5 text-right">Rolls</th>
                  <th className="px-4 py-2.5 text-right">Rent</th>
                  <th className="px-4 py-2.5 text-right">Expenses</th>
                  <th className="px-4 py-2.5 text-right">Profit</th>
                  <th className="px-4 py-2.5 text-right">Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.by_booth.map((b) => (
                  <tr key={b.kiosk_id} className="hover:bg-slate-50" data-testid={`pl-row-${b.kiosk_id}`}>
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-slate-900">{b.name}</span>
                      {!b.rent_configured && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">no rent set</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">{inr(b.net_revenue)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-500">{inr(b.gst)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-500">{inr(b.fees)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-500">{inr(b.media_cost)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-500">{inr(b.rent)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-500">{inr(b.expenses)}</td>
                    <td className={`px-4 py-2.5 text-right font-bold ${b.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{inr(b.profit)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{b.margin_pct == null ? '—' : `${b.margin_pct}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isSuper && <CostSettings token={token} kiosks={kiosks} onChanged={fetchData} />}
      <GstReportCard token={token} isSuper={isSuper} />
      <ExpenseLedger token={token} kiosks={kiosks} expenses={data.expenses} onChanged={fetchData} isSuper={isSuper} />
    </div>
  );
}

const bucketLabel = (bucket, gran) => {
  if (gran === 'month') {
    const [y, m] = bucket.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
  }
  const d = new Date(bucket + 'T00:00:00');
  const s = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return gran === 'week' ? `w/c ${s}` : s;
};

const ProfitDot = ({ cx, cy, payload }) => {
  if (cx == null || cy == null) return null;
  const loss = payload.profit < 0;
  return <circle cx={cx} cy={cy} r={loss ? 4 : 3} fill={loss ? '#e11d48' : '#10b981'} stroke="#fff" strokeWidth={1.5} />;
};

const ProfitTrendChart = ({ trend }) => {
  if (!trend || !trend.points || trend.points.length === 0) return null;
  const gran = trend.granularity;
  const rows = trend.points.map((p) => ({ ...p, label: bucketLabel(p.bucket, gran) }));
  const lossBuckets = rows.filter((r) => r.profit < 0).length;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="profit-trend-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold text-slate-900" style={HEAD}>Profit trend</h3>
          <p className="text-xs text-slate-400">Net profit per {gran} — red dots dipped into loss.</p>
        </div>
        {lossBuckets > 0 && (
          <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-rose-100 text-rose-700" data-testid="loss-bucket-badge">
            {lossBuckets} loss-making {gran}{lossBuckets > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="profitFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} interval="preserveStartEnd" minTickGap={24} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`)} width={56} />
            <Tooltip
              formatter={(v, name) => [inr(v), { profit: 'Profit', revenue: 'Revenue', costs: 'Costs' }[name] || name]}
              labelStyle={{ fontWeight: 600, fontSize: 12 }}
              contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }}
            />
            <ReferenceLine y={0} stroke="#e11d48" strokeDasharray="4 4" strokeOpacity={0.6} />
            <Area type="monotone" dataKey="profit" stroke="none" fill="url(#profitFill)" tooltipType="none" legendType="none" />
            <Line type="monotone" dataKey="revenue" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
            <Line type="monotone" dataKey="profit" stroke="#10b981" strokeWidth={2.5} dot={<ProfitDot />} activeDot={{ r: 5 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-400">
        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-emerald-500 rounded" /> Profit</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-slate-400 rounded" style={{ borderTop: '1.5px dashed #94a3b8', background: 'none' }} /> Revenue</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500" /> Loss period</span>
      </div>
    </div>
  );
};

const CostSettings = ({ token, kiosks, onChanged }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [settings, setSettings] = useState(null);
  const [rents, setRents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [rentForm, setRentForm] = useState({ kiosk_id: '', monthly_rent: '', effective_from: istTodayStr() });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/profit/settings`, { headers });
      setSettings(r.data.settings);
      setRents(r.data.rent_history || []);
    } catch (e) { /* silent */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/profit/settings`, {
        commission_pct: Number(settings.commission_pct),
        roll_price: Number(settings.roll_price),
        roll_prints: Number(settings.roll_prints),
        gst_pct: Number(settings.gst_pct),
      }, { headers });
      setSettings(r.data);
      onChanged();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed (super admin only)'); }
    setSaving(false);
  };

  const addRent = async () => {
    if (!rentForm.kiosk_id || rentForm.monthly_rent === '') return;
    try {
      await axios.post(`${API}/profit/rent`, { ...rentForm, monthly_rent: Number(rentForm.monthly_rent) }, { headers });
      setRentForm({ ...rentForm, monthly_rent: '' });
      load();
      onChanged();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const delRent = async (id) => {
    if (!window.confirm('Delete this rent entry? Historical P&L will recompute without it.')) return;
    try { await axios.delete(`${API}/profit/rent/${id}`, { headers }); load(); onChanged(); } catch (e) { alert('Failed'); }
  };

  if (!settings) return null;
  const nameOf = Object.fromEntries(kiosks.map((k) => [k.id, k.name]));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="cost-settings-card">
        <h3 className="text-sm font-bold text-slate-900 mb-1" style={HEAD}>Cost settings</h3>
        <p className="text-xs text-slate-400 mb-4">Applied to every P&L calculation.</p>
        <div className="flex items-end gap-3 flex-wrap">
          {[
            ['commission_pct', 'Razorpay %', '0.1'],
            ['gst_pct', 'GST % (incl. in price)', '0.1'],
            ['roll_price', 'Roll price (₹)', '1'],
            ['roll_prints', 'Prints per roll', '1'],
          ].map(([key, label, step]) => (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
              <input type="number" step={step} value={settings[key]}
                onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm w-32" data-testid={`profit-setting-${key}`} />
            </label>
          ))}
          <button onClick={saveSettings} disabled={saving}
            className="flex items-center gap-1.5 bg-slate-900 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50" data-testid="profit-settings-save">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">
          Effective fee {(Number(settings.commission_pct || 0) * 1.18).toFixed(2)}% · GST portion of sales {(Number(settings.gst_pct || 0) / (100 + Number(settings.gst_pct || 0)) * 100).toFixed(1)}% · cost/print ₹{Number(settings.roll_prints) ? (Number(settings.roll_price) / Number(settings.roll_prints)).toFixed(2) : '0'}
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="rent-card">
        <h3 className="text-sm font-bold text-slate-900 mb-1" style={HEAD}>Booth rent (monthly)</h3>
        <p className="text-xs text-slate-400 mb-4">Append-only history — changes apply from their effective date; past P&L stays accurate.</p>
        <div className="flex items-end gap-2 flex-wrap mb-4">
          <select value={rentForm.kiosk_id} onChange={(e) => setRentForm({ ...rentForm, kiosk_id: e.target.value })}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white" data-testid="rent-booth-select">
            <option value="">Select booth…</option>
            {kiosks.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select>
          <input type="number" placeholder="₹ / month" value={rentForm.monthly_rent}
            onChange={(e) => setRentForm({ ...rentForm, monthly_rent: e.target.value })}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm w-28" data-testid="rent-amount-input" />
          <input type="date" value={rentForm.effective_from}
            onChange={(e) => setRentForm({ ...rentForm, effective_from: e.target.value })}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm" data-testid="rent-date-input" />
          <button onClick={addRent} className="flex items-center gap-1 bg-violet-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-violet-700" data-testid="rent-add-btn">
            <Plus className="w-3.5 h-3.5" /> Set rent
          </button>
        </div>
        <div className="max-h-40 overflow-y-auto divide-y divide-slate-100 text-sm">
          {rents.length === 0 && <div className="text-xs text-slate-400 py-2">No rent configured yet.</div>}
          {rents.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-1.5" data-testid={`rent-entry-${r.id}`}>
              <span className="text-slate-700">{nameOf[r.kiosk_id] || r.kiosk_id}</span>
              <span className="font-medium">{inr(r.monthly_rent)}/mo <span className="text-xs text-slate-400">from {r.effective_from}</span></span>
              <button onClick={() => delRent(r.id)} className="text-slate-300 hover:text-rose-500" data-testid={`rent-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const GstReportCard = ({ token, isSuper }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const today = istTodayStr();
  const thisMonth = today.slice(0, 7);
  const [mode, setMode] = useState('month');
  const [month, setMonth] = useState(thisMonth);
  const [startD, setStartD] = useState(`${thisMonth}-01`);
  const [endD, setEndD] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const rangeParams = () => {
    if (mode === 'month') {
      const [y, m] = month.split('-').map(Number);
      const next = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
      return { start: `${month}-01T00:00:00+05:30`, end: `${next}-01T00:00:00+05:30` };
    }
    const endPlus = new Date(new Date(endD + 'T00:00:00Z').getTime() + 86400000).toISOString().slice(0, 10);
    return { start: `${startD}T00:00:00+05:30`, end: `${endPlus}T00:00:00+05:30` };
  };

  const load = async () => {
    setLoading(true);
    try {
      const { start, end } = rangeParams();
      const r = await axios.get(`${API}/profit/gst-report`, { headers, params: { start, end } });
      setReport(r.data);
    } catch (e) { /* silent */ }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [mode, month, startD, endD]);

  const setBoothState = async (kiosk_id, gst_state) => {
    try {
      await axios.put(`${API}/profit/booth-gst-state`, { kiosk_id, gst_state }, { headers });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed (super admin only)'); }
  };

  const downloadCsv = async () => {
    try {
      const { start, end } = rangeParams();
      const r = await axios.get(`${API}/profit/gst-report`, { headers, params: { start, end, format: 'csv' }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gst-report_${mode === 'month' ? month : `${startD}_to_${endD}`}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Download failed'); }
  };

  const t = report?.totals;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="gst-report-card">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="text-sm font-bold text-slate-900" style={HEAD}>GST report</h3>
          <p className="text-xs text-slate-400">GSTR-ready summary — Karnataka booths split CGST+SGST, other states billed as IGST. ITC = GST on Razorpay fees.</p>
        </div>
        <button onClick={downloadCsv} disabled={!report}
          className="flex items-center gap-1.5 bg-slate-900 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50" data-testid="gst-download-btn">
          <Download className="w-3.5 h-3.5" /> Download CSV
        </button>
      </div>
      <div className="flex items-end gap-2 flex-wrap mb-4">
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
          {[['month', 'Month'], ['range', 'Custom range']].map(([id, label]) => (
            <button key={id} onClick={() => setMode(id)}
              className={`px-3 py-2 ${mode === id ? 'bg-slate-900 text-white' : 'bg-white text-slate-500'}`} data-testid={`gst-mode-${id}`}>{label}</button>
          ))}
        </div>
        {mode === 'month' ? (
          <input type="month" value={month} max={thisMonth} onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm" data-testid="gst-month-input" />
        ) : (
          <>
            <input type="date" value={startD} onChange={(e) => setStartD(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm" data-testid="gst-start-input" />
            <span className="text-slate-400 text-sm pb-1.5">→</span>
            <input type="date" value={endD} onChange={(e) => setEndD(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm" data-testid="gst-end-input" />
          </>
        )}
      </div>
      {loading && !report ? (
        <div className="h-24 bg-slate-50 rounded-lg animate-pulse" />
      ) : report && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="gst-report-table">
            <thead>
              <tr className="border-y border-slate-100 bg-slate-50/60 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 text-left">Booth</th>
                <th className="px-3 py-2 text-left">State</th>
                <th className="px-3 py-2 text-right">Net Sales</th>
                <th className="px-3 py-2 text-right">Taxable</th>
                <th className="px-3 py-2 text-right">CGST</th>
                <th className="px-3 py-2 text-right">SGST</th>
                <th className="px-3 py-2 text-right">IGST</th>
                <th className="px-3 py-2 text-right">ITC</th>
                <th className="px-3 py-2 text-right">Net Payable</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {report.rows.map((r) => (
                <tr key={r.kiosk_id} data-testid={`gst-row-${r.kiosk_id}`}>
                  <td className="px-3 py-2 font-medium text-slate-900">{r.name}</td>
                  <td className="px-3 py-2">
                    {isSuper ? (
                      <select value={r.gst_state} onChange={(e) => setBoothState(r.kiosk_id, e.target.value)}
                        className="px-2 py-1 border border-slate-200 rounded-lg text-xs bg-white" data-testid={`gst-state-select-${r.kiosk_id}`}>
                        <option value="KA">Karnataka (CGST+SGST)</option>
                        <option value="OTHER">Other state (IGST)</option>
                      </select>
                    ) : (
                      <span className="text-xs text-slate-600" data-testid={`gst-state-label-${r.kiosk_id}`}>
                        {r.gst_state === 'KA' ? 'Karnataka (CGST+SGST)' : 'Other state (IGST)'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{inr(r.net_sales)}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{inr(r.taxable_value)}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{r.gst_state === 'KA' ? inr(r.cgst) : '—'}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{r.gst_state === 'KA' ? inr(r.sgst) : '—'}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{r.gst_state === 'OTHER' ? inr(r.igst) : '—'}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{inr(r.itc)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{inr(r.net_payable)}</td>
                </tr>
              ))}
            </tbody>
            {t && (
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-bold text-slate-900" data-testid="gst-totals-row">
                  <td className="px-3 py-2" colSpan={2}>TOTAL</td>
                  <td className="px-3 py-2 text-right">{inr(t.net_sales)}</td>
                  <td className="px-3 py-2 text-right">{inr(t.taxable_value)}</td>
                  <td className="px-3 py-2 text-right">{inr(t.cgst)}</td>
                  <td className="px-3 py-2 text-right">{inr(t.sgst)}</td>
                  <td className="px-3 py-2 text-right">{inr(t.igst)}</td>
                  <td className="px-3 py-2 text-right">{inr(t.itc)}</td>
                  <td className="px-3 py-2 text-right text-indigo-700">{inr(t.net_payable)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

const ExpenseLedger = ({ token, kiosks, expenses, onChanged, isSuper }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [form, setForm] = useState({ date: istTodayStr(), amount: '', category: 'misc', note: '', kiosk_id: '' });
  const [adding, setAdding] = useState(false);
  const nameOf = Object.fromEntries(kiosks.map((k) => [k.id, k.name]));

  const add = async () => {
    if (!form.amount) return;
    setAdding(true);
    try {
      await axios.post(`${API}/profit/expenses`, { ...form, amount: Number(form.amount), kiosk_id: form.kiosk_id || null, note: form.note || null }, { headers });
      setForm({ ...form, amount: '', note: '' });
      onChanged();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setAdding(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await axios.delete(`${API}/profit/expenses/${id}`, { headers }); onChanged(); } catch (e) { alert('Failed'); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="expense-ledger">
      <h3 className="text-sm font-bold text-slate-900 mb-1" style={HEAD}>Other expenses</h3>
      <p className="text-xs text-slate-400 mb-4">Manual costs — pick a booth or leave as general (whole fleet). Shown for the filtered period.</p>
      {isSuper && (
      <div className="flex items-end gap-2 flex-wrap mb-4">
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm" data-testid="expense-date-input" />
        <input type="number" placeholder="₹ amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm w-28" data-testid="expense-amount-input" />
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white capitalize" data-testid="expense-category-select">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={form.kiosk_id} onChange={(e) => setForm({ ...form, kiosk_id: e.target.value })}
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white" data-testid="expense-booth-select">
          <option value="">General (all booths)</option>
          {kiosks.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
        <input placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm w-48" data-testid="expense-note-input" />
        <button onClick={add} disabled={adding} className="flex items-center gap-1 bg-violet-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-violet-700 disabled:opacity-50" data-testid="expense-add-btn">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      )}
      {expenses.length === 0 ? (
        <div className="text-xs text-slate-400">No expenses in this period.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-slate-100 bg-slate-50/60 text-[11px] font-semibold uppercase tracking-wider text-slate-500 text-left">
                <th className="px-3 py-2">Date</th><th className="px-3 py-2">Booth</th><th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Note</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {expenses.map((e) => (
                <tr key={e.id} data-testid={`expense-row-${e.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{e.date}</td>
                  <td className="px-3 py-2">{e.kiosk_id ? (nameOf[e.kiosk_id] || e.kiosk_id) : <span className="text-slate-400">General</span>}</td>
                  <td className="px-3 py-2 capitalize">{e.category}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{e.note || '—'}</td>
                  <td className="px-3 py-2 text-right font-semibold text-rose-600">{inr(e.amount)}</td>
                  <td className="px-3 py-2 text-right">
                    {isSuper && (
                      <button onClick={() => del(e.id)} className="text-slate-300 hover:text-rose-500" data-testid={`expense-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <span className="hidden" />
    </div>
  );
};
